import { Handle, Position } from '@xyflow/react';

import React, { useMemo } from 'react';

import { AddCircle, ArrowUpward, Code, Delete, Edit, Extension, Groups, Hexagon, Info, Refresh, RocketLaunch, VpnKey } from '@mui/icons-material';
import { FormControlLabel, Switch, IconButton, Tab, Tabs, alpha, useTheme } from '@mui/material';

import { ApiKeyModel, ClientModel, ComponentModel, EnvironmentApiKeyModel, EnvironmentClientModel, EnvironmentModel } from '../../../api/api';
import ActionButtonsMenu, { ActionButton, MenuAction } from './ActionButtonsMenu';
import { getEnvironmentCloudProvider } from './Consts';
import AnimatedEnvironmentPanel from './AnimatedEnvironmentPanel';
import EnvironmentEmbedsPanel from './EnvironmentEmbedsPanel';
import { getNodePalette } from './EnvironmentNodePalette';
import { usePersistedSetting } from '../../../common/hooks/usePersistedSetting';
import { ContextSubdomains, groupComponentsBySubdomain } from './ComponentSubdomainGroups';
import { ItemPresenceIndicator } from '../../../components/Presence/PresenceContext';

interface EnvironmentNodeProps {
	data: {
		environment: EnvironmentModel;
		onEdit: (env: EnvironmentModel) => void;
		onDelete: (env: EnvironmentModel) => void;
		components: ComponentModel[];
		contextSubdomains?: ContextSubdomains;
		clients: ClientModel[];
		apiKeys: ApiKeyModel[];
		onAddEnvironmentComponent: (
			environment: EnvironmentModel,
			component: ComponentModel,
			operation: 'addComponent' | 'updateComponent' | 'redeployComponent',
		) => void;
		onRemoveEnvironmentComponent: (environment: EnvironmentModel, component: ComponentModel) => void;
		onDeleteComponent: (component: ComponentModel) => void;
		onAddEnvironmentClient?: (environment: EnvironmentModel, client: ClientModel, operation: 'addClient' | 'redeployClient') => void;
		onRemoveEnvironmentClient?: (environment: EnvironmentModel, client: ClientModel) => void;
		onAddEnvironmentApiKey?: (environment: EnvironmentModel, apiKey: ApiKeyModel, operation: 'addApiKey' | 'redeployApiKey') => void;
		onRemoveEnvironmentApiKey?: (environment: EnvironmentModel, apiKey: ApiKeyModel) => void;
		isFirstNode: boolean;
		isLastNode?: boolean;
		onAddEnvironment?: () => void;
		onViewEmbeds?: (environment: EnvironmentModel, definitionId?: string, deploymentId?: string, action?: 'remove') => void;
		onViewReleases?: (env: EnvironmentModel) => void;
		onViewDetails?: (env: EnvironmentModel) => void;
		onViewComponentDetails?: (component: ComponentModel, environmentComponent: any, environment: EnvironmentModel) => void;
		onViewClientDetails?: (client: ClientModel, environmentClient?: EnvironmentClientModel, environment?: EnvironmentModel) => void;
		onViewApiKeyDetails?: (apiKey: ApiKeyModel, environmentApiKey?: EnvironmentApiKeyModel, environment?: EnvironmentModel) => void;
		isSelectedForReleasePlans?: boolean;
		isSelectedForDetails?: boolean;
		selectedComponentForDetails?: { component: ComponentModel; environmentComponent: any } | null;
		selectedClientId?: string | null;
		selectedApiKeyId?: string | null;
		visibleLayers?: EnvironmentGraphLayer[];
		onVisibleLayersChange?: (event: React.SyntheticEvent, nextLayers: EnvironmentGraphLayer[]) => void;
	};
}

type EnvironmentGraphLayer = 'components' | 'clients' | 'apiKeys' | 'embeds';


export default function EnvironmentNode({ data }: EnvironmentNodeProps) {
	const theme = useTheme();
	const nodePalette = useMemo(() => getNodePalette(theme), [theme]);
	const {
		environment,
		onEdit,
		onDelete,
		components,
		clients,
		apiKeys,
		onAddEnvironmentComponent,
		onRemoveEnvironmentComponent,
		onDeleteComponent,
		onAddEnvironmentClient,
		onRemoveEnvironmentClient,
		onAddEnvironmentApiKey,
		onRemoveEnvironmentApiKey,
		isFirstNode,
		isLastNode,
		onAddEnvironment,
		onViewReleases,
		onViewDetails,
		onViewComponentDetails,
		onViewClientDetails,
		onViewApiKeyDetails,
		isSelectedForReleasePlans,
		isSelectedForDetails,
		selectedComponentForDetails,
		selectedClientId,
		selectedApiKeyId,
		visibleLayers = ['components'],
		onVisibleLayersChange,
	} = data;
	const activeLayer = visibleLayers[0] ?? 'components';
	const showComponents = activeLayer === 'components';
	const [groupBySubdomain, setGroupBySubdomain] = usePersistedSetting<boolean>('environments.groupBySubdomain.' + environment.id, true);
	const showClients = activeLayer === 'clients';
	const showApiKeys = activeLayer === 'apiKeys';

	const handleReleasesClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (onViewReleases) {
			onViewReleases(environment);
		}
	};

	const handleDetailsClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (onViewDetails) {
			onViewDetails(environment);
		}
	};

	const handleEditClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onEdit(environment);
	};

	const handleDeleteClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onDelete(environment);
	};

	const handleAddComponent = (e: React.MouseEvent, componentId: string) => {
		e.stopPropagation();
		const component = components.find((c) => c.id === componentId);
		if (!component) return;

		// Check if this component is already deployed but with a different version
		const deployedComponent = environment.components?.find((envComp) => envComp.componentId === component.id);
		const operation = deployedComponent ? 'updateComponent' : 'addComponent';

		onAddEnvironmentComponent(environment, component, operation);
	};

	const handleAddClient = (e: React.MouseEvent, clientId: string) => {
		e.stopPropagation();
		const client = clients.find((c) => c.id === clientId);
		if (!client || !onAddEnvironmentClient) return;

		const deployedClient = environment.clients?.find((envClient) => envClient.clientId === client.id);
		onAddEnvironmentClient(environment, client, deployedClient ? 'redeployClient' : 'addClient');
	};

	const handleAddApiKey = (e: React.MouseEvent, apiKeyId: string) => {
		e.stopPropagation();
		const apiKey = apiKeys.find((item) => item.id === apiKeyId);
		if (!apiKey || !onAddEnvironmentApiKey) return;

		const deployedApiKey = environment.apiKeys?.find((environmentApiKey) => environmentApiKey.apiKeyId === apiKey.id);
		onAddEnvironmentApiKey(environment, apiKey, deployedApiKey ? 'redeployApiKey' : 'addApiKey');
	};

	// Configure action buttons and menu items
	const mainButtons: ActionButton[] = [
		// Add Environment button (only on last node)
		...(isLastNode && onAddEnvironment
			? [
					{
						id: 'add-environment',
						icon: <AddCircle style={{ fontSize: '14px' }} />,
						title: 'Add new environment',
						onClick: (e: React.MouseEvent) => {
							e.stopPropagation();
							onAddEnvironment();
						},
						color: nodePalette.accentStrong,
						backgroundColor: nodePalette.actionSurface,
					},
				]
			: []),
		// Release Plans button
		{
			id: 'release-plans',
			icon: <RocketLaunch style={{ fontSize: '14px' }} />,
			title: 'View release plans',
			onClick: handleReleasesClick,
			color: nodePalette.accentStrong,
			backgroundColor: nodePalette.actionSurface,
			isActive: isSelectedForReleasePlans,
			activeColor: nodePalette.contrast,
			activeBackgroundColor: nodePalette.accentStrong,
		},
		// Environment Details button
		{
			id: 'environment-details',
			icon: <Info style={{ fontSize: '14px' }} />,
			title: 'View environment details',
			onClick: handleDetailsClick,
			color: nodePalette.accentStrong,
			backgroundColor: nodePalette.actionSurface,
			isActive: isSelectedForDetails,
			activeColor: nodePalette.contrast,
			activeBackgroundColor: nodePalette.accentStrong,
		},
	];

	const menuActions: MenuAction[] = [
		{
			id: 'edit',
			icon: <Edit />,
			label: 'Edit',
			onClick: handleEditClick,
			color: nodePalette.accentStrong,
		},
		{
			id: 'delete',
			icon: <Delete />,
			label: 'Delete',
			onClick: handleDeleteClick,
			color: nodePalette.error,
		},
	];

	// Separate associated and unassociated components
	const { associatedComponents, unassociatedComponents } = useMemo(() => {
		const associations: { [componentId: string]: any } = {}; // Maps component ID to environment component details
		environment.components?.forEach((envComp) => {
			associations[envComp.componentId] = envComp; // Store the full environment component
		});

		const associated = components
			.filter((comp) => associations[comp.id])
			.map((comp) => ({
				...comp,
				environmentComponent: associations[comp.id], // Store the environment component for access to URL and other fields
				containerImageVersion: associations[comp.id].containerImageVersion, // Use the image version from the association
			}))
			.sort((a, b) => a.name.localeCompare(b.name));

		// Include components that are either not associated OR have a different container image version
		const unassociated = components
			.filter((comp) => {
				const association = associations[comp.id];
				// Include if not associated at all OR if the container image version is different
				return !association || association.containerImageVersion !== comp.containerImageVersion;
			})
			.sort((a, b) => a.name.localeCompare(b.name));

		return {
			associatedComponents: associated,
			unassociatedComponents: unassociated,
		};
	}, [components, environment.components]);

	const associatedComponentGroups = useMemo(() => groupBySubdomain ? groupComponentsBySubdomain(associatedComponents, data.contextSubdomains ?? new Map()) : [{ id: 'all', name: '', components: associatedComponents }], [associatedComponents, data.contextSubdomains, groupBySubdomain]);
	const availableComponentGroups = useMemo(() => groupBySubdomain ? groupComponentsBySubdomain(unassociatedComponents, data.contextSubdomains ?? new Map()) : [{ id: 'all', name: '', components: unassociatedComponents }], [unassociatedComponents, data.contextSubdomains, groupBySubdomain]);

	const { associatedClients, unassociatedClients } = useMemo(() => {
		const associations: { [clientId: string]: EnvironmentClientModel } = {};
		environment.clients?.forEach((envClient) => {
			associations[envClient.clientId] = envClient;
		});

		const associated = clients
			.filter((client) => associations[client.id])
			.map((client) => ({
				...client,
				environmentClient: associations[client.id],
			}))
			.sort((a, b) => a.name.localeCompare(b.name));

		const unassociated = clients.filter((client) => !associations[client.id]).sort((a, b) => a.name.localeCompare(b.name));

		return {
			associatedClients: associated,
			unassociatedClients: unassociated,
		};
	}, [clients, environment.clients]);

	const { associatedApiKeys, unassociatedApiKeys } = useMemo(() => {
		const associations: { [apiKeyId: string]: EnvironmentApiKeyModel } = {};
		environment.apiKeys?.forEach((environmentApiKey) => {
			if (environmentApiKey.apiKeyId) {
				associations[environmentApiKey.apiKeyId] = environmentApiKey;
			}
		});

		const associated = apiKeys
			.filter((apiKey) => associations[apiKey.id])
			.map((apiKey) => ({
				...apiKey,
				environmentApiKey: associations[apiKey.id],
			}))
			.sort((a, b) => a.name.localeCompare(b.name));

		const unassociated = apiKeys.filter((apiKey) => !associations[apiKey.id]).sort((a, b) => a.name.localeCompare(b.name));

		return {
			associatedApiKeys: associated,
			unassociatedApiKeys: unassociated,
		};
	}, [apiKeys, environment.apiKeys]);

	// Determine highlight colors based on which panel is open (Dark Theme)
	const getHighlightColors = () => {
		if (isSelectedForReleasePlans) {
			return {
				background: nodePalette.surfaceActive,
				border: nodePalette.accent,
				shadow: nodePalette.highlightShadow,
				textColor: nodePalette.text,
			};
		}
		if (isSelectedForDetails) {
			return {
				background: nodePalette.surfaceActive,
				border: nodePalette.accent,
				shadow: nodePalette.highlightShadow,
				textColor: nodePalette.text,
			};
		}
		return {
			background: nodePalette.surface,
			border: nodePalette.border,
			shadow: nodePalette.defaultShadow,
			textColor: nodePalette.text,
		};
	};

	const highlightColors = getHighlightColors();
	const cloudProvider = getEnvironmentCloudProvider(environment.attributes);
	const CloudProviderIcon = cloudProvider?.Icon;

	return (
		<div
			style={{
				background: highlightColors.background,
				border: `2px solid ${highlightColors.border}`,
				borderRadius: '8px',
				padding: '16px',
				width: '300px',
				boxSizing: 'border-box',
				textAlign: 'center',
				boxShadow: highlightColors.shadow,
				transition: 'box-shadow 0.2s, background-color 0.2s, border-color 0.2s',
				position: 'relative',
			}}
			onMouseEnter={(e) => {
				e.currentTarget.style.boxShadow = nodePalette.hoverShadow;
			}}
			onMouseLeave={(e) => {
				e.currentTarget.style.boxShadow = highlightColors.shadow;
			}}
		>
			<div
				style={{
					fontWeight: 'bold',
					fontSize: '14px',
					marginBottom: '10px',
					color: highlightColors.textColor,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '6px',
				}}
			>
				{CloudProviderIcon && <CloudProviderIcon sx={{ width: 20, height: 20 }} titleAccess={cloudProvider.tabLabel} />}
				{environment.name}
				<ItemPresenceIndicator type="environment" id={environment.id} />
			</div>

			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					marginBottom: '10px',
					minHeight: '24px',
				}}
			>
				<Tabs
					value={activeLayer}
					onChange={(event, nextLayer: EnvironmentGraphLayer) => {
						event.stopPropagation();
						onVisibleLayersChange?.(event, [nextLayer]);
					}}
					onClick={(event) => event.stopPropagation()}
					aria-label="Environment sections"
					sx={{
						bgcolor: nodePalette.actionSurface,
						minHeight: 24,
						'& .MuiTabs-indicator': { display: 'none' },
						borderRadius: '12px',
						overflow: 'hidden',
						'& .MuiTab-root': {
							minWidth: 24,
							minHeight: 24,
							width: 24,
							height: 24,
							p: 0,
							color: nodePalette.accentStrong,
							border: 0,
							borderRadius: 0,
							'&.Mui-selected': {
								color: nodePalette.contrast,
								bgcolor: nodePalette.accentStrong,
								'&:hover': {
									bgcolor: nodePalette.accentStrong,
								},
							},
							'&:hover': {
								bgcolor: alpha(nodePalette.accentStrong, 0.2),
							},
						},
					}}
				>
					<Tab value="components" aria-label="Components" title="Components" id={`environment-${environment.id}-components-tab`} aria-controls={`environment-${environment.id}-components-panel`} icon={<Extension sx={{ fontSize: 14 }} />} />
					<Tab value="clients" aria-label="API Clients" title="API Clients" id={`environment-${environment.id}-clients-tab`} aria-controls={`environment-${environment.id}-clients-panel`} icon={<Groups sx={{ fontSize: 14 }} />} />
					<Tab value="apiKeys" aria-label="API Keys" title="API Keys" id={`environment-${environment.id}-apiKeys-tab`} aria-controls={`environment-${environment.id}-apiKeys-panel`} icon={<VpnKey sx={{ fontSize: 14 }} />} />
					<Tab value="embeds" aria-label="App Embeds" title="App Embeds" id={`environment-${environment.id}-embeds-tab`} aria-controls={`environment-${environment.id}-embeds-panel`} icon={<Code sx={{ fontSize: 14 }} />} />
				</Tabs>

				<ActionButtonsMenu mainButtons={mainButtons} menuActions={menuActions} containerStyle={{ position: 'static' }} buttonStyle={{ fontSize: '14px' }} />
			</div>

			<AnimatedEnvironmentPanel tab={activeLayer} id={`environment-${environment.id}-${activeLayer}-panel`} labelledBy={`environment-${environment.id}-${activeLayer}-tab`}>
			{showComponents && (
				<FormControlLabel
					className="nodrag nopan"
					control={<Switch size="small" checked={groupBySubdomain} onChange={(_, checked) => setGroupBySubdomain(checked)} />}
					label="Group by subdomain"
					onClick={(event) => event.stopPropagation()}
					sx={{ m: 0, display: 'flex', justifyContent: 'flex-start', '& .MuiFormControlLabel-label': { fontSize: '10px' } }}
				/>
			)}
			{/* Components lists */}
			{showComponents && (associatedComponents.length > 0 || unassociatedComponents.length > 0 || isFirstNode) && (
				<div style={{ marginTop: '8px' }}>
					{/* Associated Components - always show */}
					<div style={{ marginBottom: '8px' }}>
						<div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: nodePalette.accent }}>Deployed Components</div>
						<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
							{associatedComponents.length > 0 ? (
								associatedComponentGroups.map((group) => (
									<div key={group.id}>
										{groupBySubdomain && <div style={{ fontSize: '10px', fontWeight: 'bold', textAlign: 'left', color: nodePalette.textMuted, margin: '6px 0 4px' }}>{group.name}</div>}
										<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>{group.components.map((component) => {
									const isHighlighted = selectedComponentForDetails?.component.id === component.id;
									return (
										<div
											key={component.id}
											style={{
												fontSize: '10px',
												padding: '2px 6px',
												borderRadius: '4px',
												backgroundColor: nodePalette.itemSurface,
												color: nodePalette.text,
												border: `1px solid ${nodePalette.accentStrong}`,
												borderWidth: isHighlighted ? '2px' : '1px',
												textAlign: 'left',
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												boxShadow: isHighlighted ? nodePalette.itemHighlightShadow : 'none',
											}}
											title={`${component.name} (${component.containerImageVersion}) - Active`}
										>
											<span style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, flex: '1 1 auto' }}>
												<Hexagon style={{ fontSize: '9px', flexShrink: 0, color: nodePalette.accent }} />
												<span style={{ minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{component.name}</span>
											</span>
											<span
												style={{
													width: '48px',
													flexShrink: 0,
													textAlign: 'right',
													color: nodePalette.textMuted,
													fontSize: '9px',
													opacity: 0.78,
													fontFamily: 'monospace',
												}}
											>
												{component.containerImageVersion}
											</span>
											<div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														onAddEnvironmentComponent(environment, component, 'redeployComponent');
													}}
													sx={{
														padding: '1px',
														marginLeft: '2px',
														'& .MuiSvgIcon-root': {
															fontSize: '10px',
														},
													}}
													title={`Redeploy ${component.name} (${component.containerImageVersion})`}
												>
													<Refresh />
												</IconButton>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														if (onViewComponentDetails && (component as any).environmentComponent) {
															onViewComponentDetails(component, (component as any).environmentComponent, environment);
														}
													}}
													sx={{
														padding: '1px',
														marginLeft: '2px',
														'& .MuiSvgIcon-root': {
															fontSize: '10px',
														},
													}}
													title={`View ${component.name} details`}
												>
													<Info />
												</IconButton>
												{isFirstNode && (
													<IconButton
														size="small"
														onClick={(e) => {
															e.stopPropagation();
															onRemoveEnvironmentComponent(environment, component);
														}}
														sx={{
															padding: '1px',
															marginLeft: '2px',
															'& .MuiSvgIcon-root': {
																fontSize: '10px',
															},
														}}
														title={`Remove ${component.name} from ${environment.name}`}
													>
														<Delete />
													</IconButton>
												)}
											</div>
										</div>
									);
								})}</div>
									</div>
								))
							) : (
								<div
									style={{
										fontSize: '10px',
										padding: '4px 6px',
										borderRadius: '4px',
										backgroundColor: nodePalette.emptySurface,
										color: nodePalette.textMuted,
										border: `1px dashed ${nodePalette.emptyBorder}`,
										textAlign: 'center',
										fontStyle: 'italic',
									}}
									title="No components deployed to this environment"
								>
									No components deployed
								</div>
							)}
						</div>
					</div>

					{/* Unassociated Components - only show on first node */}
					{isFirstNode && unassociatedComponents.length > 0 && (
						<div>
							<div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: nodePalette.textMuted }}>Available Components</div>
							<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
								{availableComponentGroups.map((group) => (
									<div key={group.id}>
										{groupBySubdomain && <div style={{ fontSize: '10px', fontWeight: 'bold', textAlign: 'left', color: nodePalette.textMuted, margin: '6px 0 4px' }}>{group.name}</div>}
										<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>{group.components.map((component) => {
									// Check if this component is already deployed but with a different version
									const deployedComponent = environment.components?.find((envComp) => envComp.componentId === component.id);
									const isDifferentVersion = deployedComponent && deployedComponent.containerImageVersion !== component.containerImageVersion;

									// Keep available components visible without breaking the unified node palette.
									const componentStyles = isDifferentVersion
										? {
												background: `linear-gradient(90deg, ${alpha(nodePalette.accentStrong, 0.18)}, ${nodePalette.itemSurfaceStrongHint} 18px)`,
												color: nodePalette.text,
												border: `1px solid ${nodePalette.borderMuted}`,
												borderLeft: `3px solid ${nodePalette.accent}`,
												boxShadow: nodePalette.itemInsetShadow,
											}
										: {
												background: `linear-gradient(90deg, ${alpha(nodePalette.accentStrong, 0.1)}, ${nodePalette.itemSurfaceHint} 18px)`,
												color: nodePalette.text,
												border: `1px solid ${nodePalette.borderMuted}`,
												borderLeft: `3px solid ${nodePalette.accentStrong}`,
												boxShadow: nodePalette.itemInsetShadow,
											};

									const titleText = isDifferentVersion
										? `${component.name} (${component.containerImageVersion}) - Update from ${deployedComponent.containerImageVersion}`
										: `${component.name} (${component.containerImageVersion}) - Click arrow to add to environment`;

									return (
										<div
											key={component.id}
											style={{
												fontSize: '10px',
												padding: '2px 6px',
												borderRadius: '4px',
												...componentStyles,
												textAlign: 'left',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
											}}
											title={titleText}
										>
											<span style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, flex: '1 1 auto' }}>
												<Hexagon style={{ fontSize: '9px', flexShrink: 0, color: nodePalette.accent }} />
												<span style={{ minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{component.name}</span>
											</span>
											<span
												style={{
													width: '48px',
													flexShrink: 0,
													textAlign: 'right',
													color: nodePalette.textMuted,
													fontSize: '9px',
													opacity: 0.78,
													fontFamily: 'monospace',
												}}
											>
												{component.containerImageVersion}
											</span>
											<div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
												<IconButton
													size="small"
													onClick={(e) => handleAddComponent(e, component.id)}
													style={{
														width: '16px',
														height: '16px',
														fontSize: '8px',
														color: isDifferentVersion ? nodePalette.accent : nodePalette.textMuted,
														marginLeft: '4px',
													}}
													title={isDifferentVersion ? 'Update component version' : 'Add to environment'}
												>
													<ArrowUpward style={{ fontSize: '10px' }} />
												</IconButton>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														onDeleteComponent(component);
													}}
													sx={{
														padding: '1px',
														marginLeft: '2px',
														'& .MuiSvgIcon-root': {
															fontSize: '10px',
														},
													}}
													title={`Delete ${component.name} component`}
												>
													<Delete />
												</IconButton>
											</div>
										</div>
									);
								})}</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			)}

			{/* Clients lists */}
			{showClients && (associatedClients.length > 0 || unassociatedClients.length > 0 || isFirstNode) && (
				<div style={{ marginTop: '10px' }}>
					<div style={{ marginBottom: '8px' }}>
						<div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: nodePalette.accent }}>Deployed API Clients</div>
						<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
							{associatedClients.length > 0 ? (
								associatedClients.map((client) => {
									const environmentClient = (client as ClientModel & { environmentClient: EnvironmentClientModel }).environmentClient;
									const isHighlighted = selectedClientId === client.id;
									const hasCredentials = !!environmentClient.attributes?.['Azure:ClientId'] || !!environmentClient.attributes?.['Azure:ClientSecret'];

									return (
										<div
											key={client.id}
											style={{
												fontSize: '10px',
												padding: '2px 6px',
												borderRadius: '4px',
												backgroundColor: nodePalette.itemSurface,
												color: nodePalette.text,
												border: hasCredentials ? `1px solid ${nodePalette.accentStrong}` : `1px dashed ${nodePalette.accent}`,
												borderWidth: isHighlighted ? '2px' : '1px',
												textAlign: 'left',
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												boxShadow: isHighlighted ? nodePalette.itemHighlightShadow : 'none',
											}}
											title={`${client.name} - ${hasCredentials ? 'Deployed' : 'Pending'}`}
										>
											<span style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
												<Groups style={{ fontSize: '10px', flexShrink: 0 }} />
												<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.name}</span>
											</span>
											<div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														onAddEnvironmentClient?.(environment, client, 'redeployClient');
													}}
													sx={{
														padding: '1px',
														marginLeft: '2px',
														'& .MuiSvgIcon-root': {
															fontSize: '10px',
														},
													}}
													title={`Redeploy ${client.name}`}
												>
													<Refresh />
												</IconButton>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														onViewClientDetails?.(client, environmentClient, environment);
													}}
													sx={{
														padding: '1px',
														marginLeft: '2px',
														'& .MuiSvgIcon-root': {
															fontSize: '10px',
														},
													}}
													title={`View ${client.name} details`}
												>
													<Info />
												</IconButton>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														onRemoveEnvironmentClient?.(environment, client);
													}}
													sx={{
														padding: '1px',
														marginLeft: '2px',
														'& .MuiSvgIcon-root': {
															fontSize: '10px',
														},
													}}
													title={`Remove ${client.name} from ${environment.name}`}
												>
													<Delete />
												</IconButton>
											</div>
										</div>
									);
								})
							) : (
								<div
									style={{
										fontSize: '10px',
										padding: '4px 6px',
										borderRadius: '4px',
										backgroundColor: nodePalette.emptySurface,
										color: nodePalette.textMuted,
										border: `1px dashed ${nodePalette.emptyBorder}`,
										textAlign: 'center',
										fontStyle: 'italic',
									}}
									title="No API clients deployed to this environment"
								>
									No API clients deployed
								</div>
							)}
						</div>
					</div>

					{isFirstNode && unassociatedClients.length > 0 && (
						<div>
							<div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: nodePalette.textMuted }}>Available API Clients</div>
							<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
								{unassociatedClients.map((client) => (
									<div
										key={client.id}
										style={{
											fontSize: '10px',
											padding: '2px 6px',
											borderRadius: '4px',
											backgroundColor: nodePalette.itemSurfaceMuted,
											color: nodePalette.textMuted,
											border: `1px solid ${nodePalette.borderMuted}`,
											textAlign: 'left',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
										}}
										title={`${client.name} - Click arrow to deploy to environment`}
									>
										<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.name}</span>
										<div style={{ display: 'flex', alignItems: 'center' }}>
											<IconButton
												size="small"
												onClick={(e) => handleAddClient(e, client.id)}
												style={{
													width: '16px',
													height: '16px',
													fontSize: '8px',
													color: nodePalette.textMuted,
													marginLeft: '4px',
												}}
												title="Deploy client to environment"
											>
												<ArrowUpward style={{ fontSize: '10px' }} />
											</IconButton>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			)}

			{/* API key lists */}
			{showApiKeys && (associatedApiKeys.length > 0 || unassociatedApiKeys.length > 0 || isFirstNode) && (
				<div style={{ marginTop: '10px' }}>
					<div style={{ marginBottom: '8px' }}>
						<div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: nodePalette.accent }}>Deployed API Keys</div>
						<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
							{associatedApiKeys.length > 0 ? (
								associatedApiKeys.map((apiKey) => {
									const environmentApiKey = (apiKey as ApiKeyModel & { environmentApiKey: EnvironmentApiKeyModel }).environmentApiKey;
									const isHighlighted = selectedApiKeyId === apiKey.id;
									const hasValue = !!environmentApiKey.attributes?.['ApiKey:Value'] || !!environmentApiKey.attributes?.['ApiKey:Hash'];

									return (
										<div
											key={apiKey.id}
											style={{
												fontSize: '10px',
												padding: '2px 6px',
												borderRadius: '4px',
												backgroundColor: nodePalette.itemSurface,
												color: nodePalette.text,
												border: hasValue ? `1px solid ${nodePalette.accentStrong}` : `1px dashed ${nodePalette.accent}`,
												borderWidth: isHighlighted ? '2px' : '1px',
												textAlign: 'left',
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												boxShadow: isHighlighted ? nodePalette.itemHighlightShadow : 'none',
											}}
											title={`${apiKey.name} - ${hasValue ? 'Deployed' : 'Pending'}`}
										>
											<span style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
												<VpnKey style={{ fontSize: '10px', flexShrink: 0 }} />
												<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{apiKey.name}</span>
											</span>
											<div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														onAddEnvironmentApiKey?.(environment, apiKey, 'redeployApiKey');
													}}
													sx={{
														padding: '1px',
														marginLeft: '2px',
														'& .MuiSvgIcon-root': {
															fontSize: '10px',
														},
													}}
													title={`Redeploy ${apiKey.name}`}
												>
													<Refresh />
												</IconButton>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														onViewApiKeyDetails?.(apiKey, environmentApiKey, environment);
													}}
													sx={{
														padding: '1px',
														marginLeft: '2px',
														'& .MuiSvgIcon-root': {
															fontSize: '10px',
														},
													}}
													title={`View ${apiKey.name} details`}
												>
													<Info />
												</IconButton>
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														onRemoveEnvironmentApiKey?.(environment, apiKey);
													}}
													sx={{
														padding: '1px',
														marginLeft: '2px',
														'& .MuiSvgIcon-root': {
															fontSize: '10px',
														},
													}}
													title={`Remove ${apiKey.name} from ${environment.name}`}
												>
													<Delete />
												</IconButton>
											</div>
										</div>
									);
								})
							) : (
								<div
									style={{
										fontSize: '10px',
										padding: '4px 6px',
										borderRadius: '4px',
										backgroundColor: nodePalette.emptySurface,
										color: nodePalette.textMuted,
										border: `1px dashed ${nodePalette.emptyBorder}`,
										textAlign: 'center',
										fontStyle: 'italic',
									}}
									title="No API keys deployed to this environment"
								>
									No API keys deployed
								</div>
							)}
						</div>
					</div>

					{isFirstNode && unassociatedApiKeys.length > 0 && (
						<div>
							<div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: nodePalette.textMuted }}>Available API Keys</div>
							<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
								{unassociatedApiKeys.map((apiKey) => (
									<div
										key={apiKey.id}
										style={{
											fontSize: '10px',
											padding: '2px 6px',
											borderRadius: '4px',
											backgroundColor: nodePalette.itemSurfaceMuted,
											color: nodePalette.textMuted,
											border: `1px solid ${nodePalette.borderMuted}`,
											textAlign: 'left',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
										}}
										title={`${apiKey.name} - Click arrow to deploy to environment`}
									>
										<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{apiKey.name}</span>
										<div style={{ display: 'flex', alignItems: 'center' }}>
											<IconButton
												size="small"
												onClick={(e) => handleAddApiKey(e, apiKey.id)}
												style={{
													width: '16px',
													height: '16px',
													fontSize: '8px',
													color: nodePalette.textMuted,
													marginLeft: '4px',
												}}
												title="Deploy API key to environment"
											>
												<ArrowUpward style={{ fontSize: '10px' }} />
											</IconButton>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			)}

			{activeLayer === 'embeds' && <EnvironmentEmbedsPanel environment={environment} onSelect={data.onViewEmbeds} />}

			</AnimatedEnvironmentPanel>

			{/* Connection handles for edges */}
			<Handle
				type="target"
				position={Position.Left}
				style={{ background: nodePalette.accent, width: '8px', height: '8px', border: `2px solid ${theme.palette.divider}` }}
			/>
			<Handle
				type="source"
				position={Position.Right}
				style={{ background: nodePalette.accent, width: '8px', height: '8px', border: `2px solid ${theme.palette.divider}` }}
			/>
		</div>
	);
}
